import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import SpinnerLoader from "../../../components/SpinnerLoader";
import { RootState, useAppDispatch } from "../../../redux/store";
import {
  getHrApprovalApplications,
  updateHRApplicationStatus,
} from "../../../redux/slices/hr.applications.slice";
import HomeButton from "../../../components/HomeButton";
import Header from "../../../components/Header";
import TabNavbar from "../../../components/Tabbar";
import { HR_TABS } from "../../../utils/TabbarData";
import Pagination from "../../../components/Pagination";
import { useTranslation } from "react-i18next";
import SaveConfirmModal from "../../../components/modal/SaveConfirmModal";
import { FaX } from "react-icons/fa6";

// Terminal states this page's own actions produce — once an item is here,
// re-showing Approve/Reject would let a stale row send a no-op transition
// (e.g. APPROVED_BY_HR -> APPROVED_BY_HR) that the backend correctly rejects.
const TERMINAL_STATUSES = ["APPROVED_BY_HR", "REJECTED_BY_HR"];
const STATUS_LABELS: Record<string, string> = {
  APPROVED_BY_HR: "Approved",
  REJECTED_BY_HR: "Rejected",
};
const isActionable = (item: any) =>
  !item?.application_status || !TERMINAL_STATUSES.includes(item.application_status);

function HrApprovals() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const {
    hrApprovalApplications,
    hrApprovalApplicationsLoading,
    updateLoading,
  } = useSelector((state: RootState) => state.hrApplications);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<any>(null);
  const [approveTravel, setApproveTravel] = useState<boolean>(true);

  const totalPages =
    hrApprovalApplications?.length > 0
      ? Math.ceil(hrApprovalApplications.length / itemsPerPage)
      : 1;
  const paginatedData = hrApprovalApplications?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    dispatch(getHrApprovalApplications());
  }, [dispatch]);

  const openApproveModal = (item: any) => {
    setSelectedApplication(item);
    // travel from API: 1 = travel, 0 or null = no travel
    const managerTravel = item?.travel === 1 || item?.travel === true;
    setApproveTravel(managerTravel);
    setIsApproveModalOpen(true);
  };

  const closeApproveModal = () => {
    setIsApproveModalOpen(false);
    setSelectedApplication(null);
  };

  const openRejectModal = (item: any) => {
    setSelectedApplication(item);
    setIsRejectModalOpen(true);
  };

  const closeRejectModal = () => {
    setIsRejectModalOpen(false);
    setSelectedApplication(null);
  };

  const handleApprove = async () => {
    if (!selectedApplication) return;
    try {
      await dispatch(
        updateHRApplicationStatus({
          user_id: selectedApplication.user_id,
          course_id: selectedApplication.course_id,
          application_status: "APPROVED_BY_HR",
          travel: approveTravel,
        })
      );
      closeApproveModal();
      await dispatch(getHrApprovalApplications());
    } catch (error) {
      console.error("Error approving application:", error);
    }
  };

  const handleReject = async () => {
    if (!selectedApplication) return;
    try {
      await dispatch(
        updateHRApplicationStatus({
          user_id: selectedApplication.user_id,
          course_id: selectedApplication.course_id,
          application_status: "REJECTED_BY_HR",
        })
      );
      closeRejectModal();
      await dispatch(getHrApprovalApplications());
    } catch (error) {
      console.error("Error rejecting application:", error);
    }
  };

  const getLearnerFullName = (item: any) => item?.full_name ?? "—";

  const getCareerLevel = (item: any) => item?.career_level ?? "—";

  const getBranchName = (item: any) => item?.branch_name ?? "—";

  const getGroupEmployeesFlag = (item: any) =>
    item?.is_group_employee === true ? t("Yes") : t("No");

  const getManagerTravelChoice = (item: any) => {
    // 1 = Travel, 0 or null = No Travel
    return item?.travel === 1 || item?.travel === true ? t("Travel") : t("No Travel");
  };

  const isMobileOrTablet = window.innerWidth < 1024;

  return (
    <div className="flex flex-col items-start gap-8">
      {!isMobileOrTablet && <HomeButton />}
      {isMobileOrTablet ? (
        <>
          <TabNavbar
            id=""
            tabs={HR_TABS}
            selectedTabId={3}
            type="HR Approvals"
          />
          <HomeButton />
          <Header />
        </>
      ) : (
        <>
          <Header />
          <TabNavbar
            id=""
            tabs={HR_TABS}
            selectedTabId={3}
            type="HR Approvals"
          />
        </>
      )}

      <div className="w-full flex flex-col gap-4">
        {/* Mobile/Tablet View */}
        <div className="block md:hidden w-full h-full">
          {hrApprovalApplicationsLoading ? (
            <div className="flex items-center w-full justify-center mt-4 mb-4">
              <SpinnerLoader />
            </div>
          ) : paginatedData?.length > 0 ? (
            paginatedData.map((item: any, i: number) => (
              <div
                key={i}
                className="border border-[#c6c6c6] py-2 text-left bg-[#f3f3f3] mt-2"
              >
                <div className="px-4 border-b border-[#c6c6c6] pb-2">
                  <h3 className="font-[600] pb-2">{t("Full Name")}</h3>
                  <p className="text-lg">{getLearnerFullName(item)}</p>
                </div>
                <div className="px-4 border-b border-[#c6c6c6] pb-2">
                  <h3 className="font-[600] pb-2">{t("Career Level")}</h3>
                  <p className="text-lg">{getCareerLevel(item)}</p>
                </div>
                <div className="px-4 border-b border-[#c6c6c6] pb-2">
                  <h3 className="font-[600] pb-2">{t("Branch Name")}</h3>
                  <p className="text-lg">{getBranchName(item)}</p>
                </div>
                <div className="px-4 border-b border-[#c6c6c6] pb-2">
                  <h3 className="font-[600] pb-2">
                    {t("Group Employees")}
                  </h3>
                  <p className="text-lg">{getGroupEmployeesFlag(item)}</p>
                </div>
                <div className="px-4 border-b border-[#c6c6c6] pb-2">
                  <h3 className="font-[600] pb-2">{t("Course Name")}</h3>
                  <p className="text-lg">{item?.course_name ?? "—"}</p>
                </div>
                <div className="px-4 border-b border-[#c6c6c6] pb-2">
                  <h3 className="font-[600] pb-2">{t("Travel/No Travel")}</h3>
                  <p className="text-lg">{getManagerTravelChoice(item)}</p>
                </div>
                <div className="px-4 border-b border-[#c6c6c6] pb-2">
                  <h3 className="font-[600] pb-2">{t("Action")}</h3>
                  <div className="flex gap-2 justify-start flex-wrap pt-2">
                  {isActionable(item) ? (
                    <>
                      <button
                        onClick={() => openApproveModal(item)}
                        className="px-4 py-1 border border-green-500 text-green-600 rounded-md bg-white hover:bg-green-50 transition"
                      >
                        {t("Approve")}
                      </button>
                      <button
                        className="p-2 text-red-500 hover:text-red-700"
                        onClick={() => openRejectModal(item)}
                      >
                        <FaX size={20} />
                      </button>
                    </>
                  ) : (
                    <span className="text-sm text-gray-500">
                      {t(STATUS_LABELS[item.application_status] ?? item.application_status)}
                    </span>
                  )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p className="w-full text-center py-5">
              {t("No Application Data found")}
            </p>
          )}
        </div>

        {/* Desktop View */}
        <div className="hidden md:block w-full h-full">
          <table className="table-auto w-full">
            <thead>
              <tr className="bg-[#A6192E] text-white">
                <th className="border-b border-[#961e38] py-2 px-4 w-1/5 text-center">
                  {t("Full Name")}
                </th>
                <th className="border-b border-[#961e38] py-2 px-4 w-1/5 text-center">
                  {t("Career Level")}
                </th>
                <th className="border-b border-[#961e38] py-2 px-4 w-1/5 text-center">
                  {t("Branch Name")}
                </th>
                <th className="border-b border-[#961e38] py-2 px-4 w-1/5 text-center">
                  {t("Group Employees")}
                </th>
                <th className="border-b border-[#961e38] py-2 px-4 w-1/5 text-center">
                  {t("Course Name")}
                </th>
                <th className="border-b border-[#961e38] py-2 px-4 w-1/5 text-center">
                  {t("Travel/No Travel")}
                </th>
                <th className="border-b border-[#961e38] py-2 px-4 text-center w-auto">
                  {t("Action")}
                </th>
              </tr>
            </thead>
            <tbody>
              {hrApprovalApplicationsLoading ? (
                <tr>
                  <td colSpan={7} className="text-center">
                    <div className="flex items-center w-full justify-center mt-4 mb-4">
                      <SpinnerLoader />
                    </div>
                  </td>
                </tr>
              ) : paginatedData?.length > 0 ? (
                paginatedData.map((item: any, i: number) => (
                  <tr key={i}>
                    <td className="border-b border-[#c6c6c6] py-2 px-4 text-center w-1/5">
                      {getLearnerFullName(item)}
                    </td>
                    <td className="border-b border-[#c6c6c6] py-2 px-4 text-center w-1/5">
                      {getCareerLevel(item)}
                    </td>
                    <td className="border-b border-[#c6c6c6] py-2 px-4 text-center w-1/5">
                      {getBranchName(item)}
                    </td>
                    <td className="border-b border-[#c6c6c6] py-2 px-4 text-center w-1/5">
                      {getGroupEmployeesFlag(item)}
                    </td>
                    <td className="border-b border-[#c6c6c6] py-2 px-4 text-center w-1/5">
                      {item?.course_name ?? "—"}
                    </td>
                    <td className="border-b border-[#c6c6c6] py-2 px-4 text-center w-1/5">
                      {getManagerTravelChoice(item)}
                    </td>
                    <td className="border-b border-[#c6c6c6] py-2 px-4 text-center w-auto">
                      <div className="flex justify-center gap-2">
                        {isActionable(item) ? (
                          <>
                            <button
                              onClick={() => openApproveModal(item)}
                              className="px-4 py-1 border border-green-500 text-green-600 rounded-md bg-white hover:bg-green-50 transition"
                            >
                              {t("Approve")}
                            </button>
                            <button
                              className="p-2 text-red-500 hover:text-red-700"
                              onClick={() => openRejectModal(item)}
                            >
                              <FaX size={20} />
                            </button>
                          </>
                        ) : (
                          <span className="text-sm text-gray-500">
                            {t(STATUS_LABELS[item.application_status] ?? item.application_status)}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="text-center py-5">
                    {t("No Application Data found")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {hrApprovalApplicationsLoading === false && (
        <Pagination
          totalPages={totalPages}
          _currentPage={currentPage}
          _itemsPerPage={itemsPerPage}
          total_item_count={hrApprovalApplications?.length ?? 0}
          handleChange={({ page, itemsPerPage }) => {
            setCurrentPage(page);
            setItemsPerPage(itemsPerPage);
          }}
          isCoursePage={true}
        />
      )}

      {/* Approve Modal: same style as other confirmations, with travel selector */}
      {isApproveModalOpen && selectedApplication && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-[90%] sm:w-[420px]">
            <h2 className="text-lg font-semibold mb-4">{t("Confirmation")}</h2>
            <p
              className="text-gray-700 mb-2"
              dangerouslySetInnerHTML={{
                __html: t(
                  "Are you sure you want to approve the application of USER_NAME for COURSE_NAME?"
                )
                  .replace(
                    "USER_NAME",
                    `<strong>${getLearnerFullName(selectedApplication)}</strong>`
                  )
                  .replace(
                    "COURSE_NAME",
                    `<strong>${selectedApplication?.course_name ?? ""}</strong>`
                  ),
              }}
            />
            <div className="mb-4 p-3 bg-gray-50 rounded border">
              <p className="text-sm font-medium text-gray-700 mb-2">
                {t("Manager choice (Travel / No Travel)")}:{" "}
                <strong>{getManagerTravelChoice(selectedApplication)}</strong>
              </p>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("New value (Travel / No Travel)")}:
              </label>
              <select
                className="border p-2 rounded w-full"
                value={approveTravel ? "travel" : "no_travel"}
                onChange={(e) =>
                  setApproveTravel(e.target.value === "travel")
                }
              >
                <option value="travel">{t("Travel")}</option>
                <option value="no_travel">{t("No Travel")}</option>
              </select>
            </div>
            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                onClick={closeApproveModal}
              >
                {t("Close")}
              </button>
              <button
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                onClick={handleApprove}
                disabled={updateLoading}
              >
                {updateLoading ? t("Confirming...") : t("Approve")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal: same style as User Applications reject */}
      {isRejectModalOpen && selectedApplication && (
        <SaveConfirmModal
          closeModal={closeRejectModal}
          handleSave={handleReject}
          loading={updateLoading}
          saveBtnText={t("DELETE APPLICATION")}
          cancleBtnText={t("GO BACK")}
          msg={t("Are you sure you want to reject the application of") +
            " " +
            "<strong>" +
            getLearnerFullName(selectedApplication) +
            "</strong> " +
            t("for") +
            " " +
            "<strong>" +
            selectedApplication?.course_name +
            "</strong>?"}
        />
      )}
    </div>
  );
}

export default HrApprovals;
